// Puerto de packages/hce/src/hce/SelectorFormularios.tsx — mismo picker de
// Tramo A/B/farmacia interna. Los códigos portados a `MotorFormulario` usan
// `<Link>` real de React Router (hoy son los 51; packages/hce se retiró el
// 2026-09-13, así que un código fuera de la lista no tiene enlace).
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { InsigniaFormularioPropio } from '../components/InsigniaFormularioPropio';
import { FORMULARIOS_DISCRETOS } from '../formulario/discretos';
import { codigosFormularioDeEspecialidad } from '../lib/catalogoEspecialidades';
import { TRAMO_B } from '../lib/tramoB';
import { useEspecialidadDelPracticante } from '../lib/useEspecialidadDelPracticante';
import { estaHabilitado, useEspecialidadesHabilitadas } from '../lib/useEspecialidadesHabilitadas';

const TRAMO_A = ['001', '002', '003', '005', '006', '007', '008', '020', '022', '024'];

// Grupo 1 del Módulo H — los 20 códigos con Questionnaire genérico
// (CODIGOS_CON_QUESTIONNAIRE de HceFormularioPage.tsx en packages/hce),
// verificados contra MotorFormulario, más 024 (Grupo 2, wizard con firma de
// paciente — MotorFormularioConsentimiento), 001 (Grupo 3, edita un
// Encounter existente — Formulario001Page) y los 30 códigos discretos del
// Grupo 4 (FORMULARIOS_DISCRETOS — construyen su propio recurso FHIR
// directo, ver formulario/discretos/index.ts). Con esto no queda ningún
// código de los 51 sin migrar — ver la regla dura de "cero links nuevos a
// packages/hce" más arriba.
const CODIGOS_MIGRADOS = new Set([
  '001',
  '002',
  '003',
  '008',
  '038',
  '017',
  '018',
  '060',
  '051',
  '056',
  '056A',
  '056B',
  '057',
  '057A',
  '033',
  '121',
  '059',
  '024',
  // 054/055 son discretos del Grupo 4 (Anexo 1 MSP) — ya vienen incluidos
  // acá vía FORMULARIOS_DISCRETOS, no se listan aparte (ver tramoB.ts).
  ...Object.keys(FORMULARIOS_DISCRETOS),
  // Grupo 5 — FEMO/escalas clínicas, sí Questionnaire nuevos (ver
  // catalogoEspecialidades.ts para el namespace de las escalas).
  'FEMO',
  'esc-phq9',
  'esc-nyha',
  'esc-glasgow',
]);

function LinkFormulario({ id, codigo, qs, children }: { id: string; codigo: string; qs: URLSearchParams; children: JSX.Element | string }): JSX.Element {
  if (CODIGOS_MIGRADOS.has(codigo)) {
    // Ruta absoluta (no relativa a la ruta activa): SelectorFormularios se
    // renderiza desde varios tabs (Notas, Laboratorio y Estudios,
    // Documentos, cada GrupoFormularioTab), y "formulario/:codigo" es
    // HERMANA de esas rutas dentro de :id, no hija — un `to` relativo
    // anidaba mal (ej. ".../notas/formulario/001", que no matchea ninguna
    // ruta) y dejaba la app en blanco. Bug real encontrado en verificación
    // en vivo del Grupo 3 2026-09-01, afecta a TODOS los códigos migrados,
    // no solo 001.
    return (
      <span className="flex flex-wrap items-center gap-2">
        <Link className="text-sm text-hce-pine underline-offset-2 hover:underline" to={`/${id}/formulario/${codigo}?${qs.toString()}`}>
          {children}
        </Link>
        <InsigniaFormularioPropio codigo={codigo} />
      </span>
    );
  }
  // Un código sin migrar (hoy ninguno) se muestra sin enlace: la app vieja se
  // retiró el 2026-09-13 y ya no hay adónde mandarlo.
  return (
    <span className="flex flex-wrap items-center gap-2">
      <span className="text-sm text-hce-muted">{children}</span>
      <InsigniaFormularioPropio codigo={codigo} />
    </span>
  );
}

export interface SelectorFormulariosProps {
  readonly subject?: string;
  readonly encounter?: string;
  readonly soloTramoA?: boolean;
  readonly soloGrupo?: string;
  /**
   * Códigos "sueltos" que el practicante tiene por `extras` en el catálogo
   * pero cuyo grupo Tramo B no le corresponde por paquete (ej. esc-phq9
   * para Medicina General) — se muestran junto a Tramo A, arriba de todo,
   * en vez de destrabar una pestaña de grupo ajena. Ver
   * `extrasSueltos` en gruposHabilitados.ts. Ausente/vacío = no hay
   * ninguno (o `soloGrupo`, donde no aplica).
   */
  readonly extrasSueltos?: string[];
}

export function SelectorFormularios({ subject, encounter, soloTramoA, soloGrupo, extrasSueltos }: SelectorFormulariosProps): JSX.Element {
  const { t } = useTranslation();
  const { id } = useParams() as { id: string };
  const estado = useEspecialidadesHabilitadas();
  const especialidadPracticante = useEspecialidadDelPracticante();

  const qs = new URLSearchParams();
  if (subject) qs.set('subject', subject);
  if (encounter) qs.set('encounter', encounter);

  const grupos = soloGrupo ? [soloGrupo] : Array.from(new Set(TRAMO_B.map((e) => e.grupo)));
  // 001 EDITA un Encounter existente (ver Formulario001.tsx en packages/hce)
  // en vez de crear un QuestionnaireResponse — sin encounter en la URL, el
  // link queda muerto. Solo "Iniciar nueva consulta" en PatientChartPage.tsx
  // asegura uno; acá se oculta en vez de mostrar un link que no funciona.
  // Bug real encontrado en verificación en vivo 2026-09-03: esta lista no
  // filtraba por especialidad del practicante, a diferencia del gate que sí
  // aplican MotorFormularioPage/FormularioDiscretoPage/Formulario001Page al
  // abrir el link — códigos exclusivos de un paquete (003/006/020
  // hospitalización, 008 emergencia) quedaban listados y clicables para
  // CUALQUIER especialidad, pero al abrirlos caían en el Alert "Fuera de tu
  // especialidad". Mismo criterio fail-closed que esos gates: sin
  // especialidad resuelta, no se lista nada de Tramo A.
  const codigosPermitidos = especialidadPracticante.cargando || !especialidadPracticante.codigo
    ? []
    : codigosFormularioDeEspecialidad(especialidadPracticante.codigo);
  const tramoAVisible = (encounter ? TRAMO_A : TRAMO_A.filter((codigo) => codigo !== '001')).filter((codigo) =>
    codigosPermitidos.includes(codigo)
  );
  const extrasSueltosVisibles = estado.cargando ? [] : (extrasSueltos ?? []).filter((c) => estaHabilitado(c, estado.codigos));

  return (
    <div className="flex flex-col gap-4">
      {!soloGrupo && (
        <ul className="flex flex-col gap-1">
          {tramoAVisible.map((codigo) => (
            <li key={codigo}>
              <LinkFormulario id={id} codigo={codigo} qs={qs}>
                {t(`hub.formulario.${codigo}`)}
              </LinkFormulario>
            </li>
          ))}
          {extrasSueltosVisibles.map((codigo) => (
            <li key={codigo}>
              <LinkFormulario id={id} codigo={codigo} qs={qs}>
                {t(`hub.formulario.${codigo}`)}
              </LinkFormulario>
            </li>
          ))}
        </ul>
      )}

      {!soloTramoA &&
        !estado.cargando &&
        grupos.map((grupo) => {
          // Mismo bug que Tramo A (ver el comentario largo arriba, línea
          // ~117): este filtro solo chequeaba `estaHabilitado` (tenant-wide,
          // "qué existe en absoluto para el tenant") pero MotorFormularioPage/
          // FormularioDiscretoPage gatean por `codigosPermitidos`
          // (por-practicante) al ABRIR el link — códigos de paquete que el
          // tenant tiene habilitado pero la especialidad del practicante NO
          // (ej. 013A/013B/118/054/055 sin Laboratorio Clínico/Patología/etc.,
          // 116-cert sin Geriatría) quedaban listados y clicables, y caían en
          // "Fuera de tu especialidad" al abrirlos. Bug real reportado en vivo
          // 2026-09-04, misma familia que el de Tramo A, sin cubrir hasta acá.
          const codigosDelGrupo = TRAMO_B.filter((e) => e.grupo === grupo && estaHabilitado(e.codigo, estado.codigos) && codigosPermitidos.includes(e.codigo));
          if (codigosDelGrupo.length === 0) return null;
          return (
            <div key={grupo} className="flex flex-col gap-1">
              <h5 className="text-sm font-semibold">{t(`hub.grupo.${grupo}`)}</h5>
              <ul className="flex flex-col gap-1">
                {codigosDelGrupo.map(({ codigo }) => (
                  <li key={codigo}>
                    <LinkFormulario id={id} codigo={codigo} qs={qs}>
                      {t(`hub.formulario.${codigo}`)}
                    </LinkFormulario>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}

    </div>
  );
}
